import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import {
  adminDeleteDeviceScreenshotsMutation,
  adminDeviceScreenshotsInfiniteOptions,
} from '@/client/@tanstack/react-query.gen'
import { ClearCaptureAction } from '@/components/features/clear-capture-action'
import { Button } from '@/components/ui/button'
import { nextOffset } from '@/lib/offset-paging'
import { ADMIN_CAPTURES_POLL_MS } from '@/lib/polling'

export function ScreenshotGallery({ deviceId }: { deviceId: string }) {
  const queryClient = useQueryClient()
  const options = adminDeviceScreenshotsInfiniteOptions({ path: { device_id: deviceId } })
  const screenshots = useInfiniteQuery({
    ...options,
    initialPageParam: 0,
    getNextPageParam: nextOffset,
    refetchInterval: ADMIN_CAPTURES_POLL_MS,
  })
  const clear = useMutation({
    ...adminDeleteDeviceScreenshotsMutation(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: options.queryKey }),
  })
  const hasItems = (screenshots.data?.pages ?? []).some((page) => page.items.length > 0)
  const items = screenshots.data?.pages.flatMap((page) => page.items) ?? []

  return (
    <div className="space-y-3">
      {hasItems && (
        <div className="flex justify-end">
          <ClearCaptureAction
            trigger="Delete all screenshots"
            title="Delete all screenshots?"
            description="This permanently removes every stored screenshot for this device, including what its parents see."
            confirmLabel="Delete all"
            pending={clear.isPending}
            onConfirm={() => clear.mutate({ path: { device_id: deviceId } })}
          />
        </div>
      )}
      {screenshots.isSuccess && items.length === 0 && (
        <p className="text-muted-foreground">No screenshots yet.</p>
      )}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {items.map((shot) => (
          <a
            key={shot.id}
            href={`/api/admin/screenshots/${shot.id}/image`}
            target="_blank"
            rel="noreferrer"
          >
            <img
              src={`/api/admin/screenshots/${shot.id}/image`}
              alt={`Screenshot from ${new Date(shot.captured_at).toLocaleString()}`}
              loading="lazy"
              className="aspect-video w-full rounded-md border object-cover"
            />
          </a>
        ))}
      </div>
      {screenshots.hasNextPage && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => void screenshots.fetchNextPage()}
          disabled={screenshots.isFetchingNextPage}
        >
          Load more
        </Button>
      )}
    </div>
  )
}
